<script lang="ts">
	import "../assets/css/main.css";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getContext, onMount } from "svelte";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";
	import { titleContextKey } from "../contexts/titleContextKey.js";
	import { descriptionContextKey } from "../contexts/descriptionContextKey.js";
	import Footer from "./Footer.svelte";
	import CookiesConsent from "./CookiesConsent.svelte";
	import Header from "./Header.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import TrackingServices from "./TrackingServices.svelte";
	import { appName } from "../../../core/schema/appName.js";
	import { scrollLockStore } from "../stores/scrollLockStore.js";
	import { createTitle } from "../utils/createTitle.js";
	import { keywordsContextKey } from "../contexts/keywordsContextKey.js";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { localeLangMap } from "../../../core/schema/localeLangMap.js";
	import { tenants } from "../../../core/config/tenants.js";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { getTenant } from "../contexts/tenantContextKey.js";
	import interRegular from "../../../core/assets/fonts/inter/Inter-Regular.woff2";
	import interMedium from "../../../core/assets/fonts/inter/Inter-Medium.woff2";
	import interSemibold from "../../../core/assets/fonts/inter/Inter-SemiBold.woff2";
	import interBold from "../../../core/assets/fonts/inter/Inter-Bold.woff2";
	import onestSemibold from "../../../core/assets/fonts/onest/Onest-SemiBold.woff2";
	import onestMedium from "../../../core/assets/fonts/onest/Onest-Medium.woff2";
	import { Locale } from "../../../core/schema/Locale";
	import { setCookie } from "../utils/setCookie";

	const { ROBOTS, WEB_URL, ENV_DOMAIN, MAIN_DOMAIN } = getGlobalSettings();

	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);
	const title = createTitle(getContext<GlobalTemplateProps["title"]>(titleContextKey));
	const description = getContext<GlobalTemplateProps["description"]>(descriptionContextKey); // TODO ?? defaultDescription
	const keywords = getContext<GlobalTemplateProps["keywords"]>(keywordsContextKey);
	const ogImage = getContext<string | undefined>(thumbnailImageContextKey); // TODO ?? defaultImage
	const { locale } = getTenant();
	const canonical = `${WEB_URL}/${pageSlug[locale] ?? ""}`;

	const jamujamLocale = locale === Locale.cs ? "cz" : "en";

	onMount(() => {
		setCookie("lang", jamujamLocale, 365, ENV_DOMAIN + MAIN_DOMAIN);
	});

	$: {
		document.documentElement.style.overflow = $scrollLockStore ? "hidden" : "auto";
	}
	const hrefOnPricingAnchor = "#pricing";
</script>

<svelte:head>
	<!-- Preloads of fonts used the top pages -->
	<!-- TODO Revise... -->
	{#each [onestSemibold, onestMedium, interRegular, interMedium, interSemibold, interBold] as font}
		<link rel="preload" as="font" type="font/woff2" href={font} crossorigin="anonymous" />
	{/each}

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	<meta property="og:url" content={canonical} />
	{#if ogImage}
		<meta property="og:image" content={ogImage} />
	{/if}

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL} />
	<meta property="twitter:url" content={canonical} />
	{#if ogImage}
		<meta name="twitter:image" content={ogImage} />
	{/if}

	<!-- Title, description and keywords -->
	<title>{title}</title>
	<meta property="og:title" content={title} />
	<meta property="og:title" content={title} />
	<meta name="twitter:title" content={title} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />
	{#if keywords}
		<meta name="keywords" content={keywords} />
	{/if}

	<!-- Canonical and hreflang -->
	<link rel="canonical" href={canonical} />
	{#each Object.values(tenants) as tenant}
		{#if tenant.locale in pageSlug}
			<link rel="alternate" hreflang={localeLangMap[tenant.locale]} href="{WEB_URL}/{pageSlug[tenant.locale]}" />
		{/if}
	{/each}

	<!-- Robots -->
	<meta name="robots" content={`${ROBOTS}, ${ROBOTS === "noindex" ? "no" : ""}follow`} />
</svelte:head>

<HeadFavicons />

<TrackingServices />

<div class="contents">
	<Header {hrefOnPricingAnchor} />

	<main class="grow 2xl:mx-auto 2xl:w-full">
		<slot />
	</main>

	<Footer />
</div>

<CookiesConsent />
